import type { DocumentCheck } from "../api";
import type { StatementStatus } from "../components/statementStatus";

export const MAX_MERCHANT_MONTHS = 3;

const parseMonth = (label: string): Date | null => {
	const date = new Date(`1 ${label.replace(" (upload date)", "")}`);
	return Number.isNaN(date.getTime()) ? null : date;
};

const short = (date: Date) =>
	date.toLocaleDateString("en-US", { month: "short" });

/** "Aug–Oct 2026" from month labels like "August 2026"; "Nov 2025–Jan 2026" across years. */
export const monthRangeLabel = (months: string[]): string => {
	const dates = months
		.map(parseMonth)
		.filter((d): d is Date => d !== null)
		.sort((a, b) => a.getTime() - b.getTime());
	if (dates.length === 0) return "";
	const first = dates[0];
	const last = dates[dates.length - 1];
	if (first.getTime() === last.getTime())
		return `${short(first)} ${first.getFullYear()}`;
	if (first.getFullYear() === last.getFullYear())
		return `${short(first)}–${short(last)} ${last.getFullYear()}`;
	return `${short(first)} ${first.getFullYear()}–${short(last)} ${last.getFullYear()}`;
};

/** Same rule as the API's record_status: any month running → processing; all done → completed. */
export const groupStatus = (
	statuses: string[],
): "processing" | "completed" | "partial" | "failed" => {
	if (statuses.some((s) => s === "processing" || s === "uploaded"))
		return "processing";
	const done = statuses.filter((s) => s === "completed").length;
	if (statuses.length && done === statuses.length) return "completed";
	return done ? "partial" : "failed";
};

export const statementsLabel = (count: number, months: string[]) => {
	const range = monthRangeLabel(months);
	return `${count} statement${count === 1 ? "" : "s"}${range ? ` · ${range}` : ""}`;
};

// Quote-blocking codes, as blocks_quote in the API's document_check.py: such a month is
// not in the record's average.
const QUOTE_BLOCKING = ["NOT_A_STATEMENT", "MISSING_PAGES"];

/** The statements-list status of a whole record, by the same rule as the record page. */
export const recordRowStatus = (
	months: { status: string; document_check?: DocumentCheck | null }[],
): StatementStatus => {
	const statuses = months.map((m) => {
		const blocked =
			m.document_check?.status === "flagged" &&
			m.document_check.issues.some((i) => QUOTE_BLOCKING.includes(i.code));
		return m.status === "completed" && blocked ? "failed" : m.status;
	});
	switch (groupStatus(statuses)) {
		case "processing":
			return { label: "Processing", group: "waiting", tone: "proc" };
		case "completed":
			return { label: "Processed", group: "ready", tone: "ok" };
		case "partial": {
			const out = statuses.filter((s) => s !== "completed").length;
			return {
				label: "Needs attention",
				group: "attention",
				tone: "hard",
				warning: `${out} of ${months.length} months are not in the average`,
			};
		}
		default:
			return { label: "Failed", group: "attention", tone: "stop" };
	}
};
